import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress,
  Alert, Chip, LinearProgress, Avatar, useTheme
} from '@mui/material';
import { TrendingUp, People, Description, CheckCircle, Person } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';

// ─── Tasarım sabitleri ────────────────────────────────────────────────────────
const BRAND  = '#1A3A6B';

const AVATAR_PALETTE = ['#6366f1','#f59e0b','#10b981','#3b82f6','#8b5cf6','#ec4899','#14b8a6','#ef4444'];
const avatarColor = (name: string) => {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};

interface CustomerStats {
  companyName: string;
  totalOffers: number;
  acceptedOffers: number;
  rejectedOffers: number;
  pendingOffers: number;
  totalRevenue: number;
  acceptanceRate: number;
}

interface UserStats {
  fullName: string;
  username: string;
  totalOffers: number;
  acceptedOffers: number;
  rejectedOffers: number;
  draftOffers: number;
  sentOffers: number;
  totalRevenue: number;
  avgProfitMargin: number;
  acceptanceRate: number;
}

interface OverallStats {
  totalOffers: number;
  totalCustomers: number;
  totalProducts: number;
  totalUsers: number;
  totalRevenue: number;
  acceptanceRate: number;
  avgProfitMargin: number;
}

interface StatsReport {
  customerStats: CustomerStats[];
  userStats: UserStats[];
  overall: OverallStats;
}

const fmt = (n: number) => n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── StatCard ─────────────────────────────────────────────────────────────────
const StatCard: React.FC<{ label: string; value: string | number; color: string; icon: React.ReactNode; sub?: string }> =
  ({ label, value, color, icon, sub }) => (
    <Paper elevation={0} sx={{
      p: 2.5, flex: 1, minWidth: 160,
      border: '1px solid', borderColor: 'divider',
      borderTop: `3px solid ${color}`,
      borderRadius: '10px',
      transition: 'box-shadow .15s',
      '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,.06)' },
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography sx={{
            fontSize: '11px', fontWeight: 600, letterSpacing: '0.05em',
            textTransform: 'uppercase', color: 'text.secondary', mb: 0.75
          }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1, mb: 0.4 }}>
            {value}
          </Typography>
          {sub && <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>{sub}</Typography>}
        </Box>
        <Box sx={{
          width: 38, height: 38, borderRadius: '10px',
          bgcolor: color + '15',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color
        }}>
          {icon}
        </Box>
      </Box>
    </Paper>
  );

// ─── RateBar ──────────────────────────────────────────────────────────────────
const RateBar: React.FC<{ rate: number }> = ({ rate }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <LinearProgress variant="determinate" value={rate}
      sx={{ flex: 1, height: 6, borderRadius: '3px', bgcolor: '#E2E8F0',
        '& .MuiLinearProgress-bar': {
          borderRadius: '3px',
          bgcolor: rate >= 50 ? '#16A34A' : rate >= 25 ? '#D97706' : '#DC2626'
        }
      }} />
    <Typography sx={{
      fontSize: '12px', fontWeight: 700, fontFamily: 'monospace',
      minWidth: 40,
      color: rate >= 50 ? '#16A34A' : rate >= 25 ? '#D97706' : '#DC2626'
    }}>
      %{rate}
    </Typography>
  </Box>
);

// ─── Click Chip ───────────────────────────────────────────────────────────────
const statusChipStyles: Record<string, any> = {
  brand: { bgcolor: BRAND, color: 'white' },
  warning: { bgcolor: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' },
  info: { bgcolor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' },
  success: { bgcolor: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' },
  error: { bgcolor: '#fff0f0', color: '#dc2626', border: '1px solid #fecaca' },
};

const ClickChip: React.FC<{ onClick: () => void; label: React.ReactNode; variant?: keyof typeof statusChipStyles }> =
  ({ onClick, label, variant = 'brand' }) => (
    <Chip label={label} size="small" onClick={onClick}
      sx={{
        cursor: 'pointer', fontWeight: 700, fontSize: '11px',
        height: 22, borderRadius: '6px',
        ...statusChipStyles[variant],
        '&:hover': { opacity: 0.85, transform: 'scale(1.05)' },
        transition: 'all 0.15s'
      }} />
  );

const getInitials = (name: string) =>
  (name || '').trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2);

const StatsPage: React.FC = () => {
  const theme   = useTheme();
  const BORDER  = theme.palette.divider;
  const SURFACE = theme.palette.background.paper;
  const TEXT2   = theme.palette.text.secondary;

  const [stats, setStats] = useState<StatsReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/admin/stats')
      .then(r => setStats(r.data))
      .catch(() => setError('İstatistikler yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress sx={{ color: BRAND }} /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  if (!stats) return null;

  const { overall, customerStats, userStats } = stats;

  const goOffers = (params: { user?: string; status?: string; customer?: string }) => {
    const q = new URLSearchParams();
    if (params.user) q.set('user', params.user);
    if (params.status) q.set('status', params.status);
    if (params.customer) q.set('customer', params.customer);
    navigate(`/offers?${q.toString()}`);
  };

  return (
    <Box sx={{ p: 3, bgcolor: theme.palette.background.default, minHeight: '100vh' }}>

      {/* ── Header ── */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}>
          İstatistik Raporu
        </Typography>
        <Typography sx={{ fontSize: '13px', color: TEXT2, mt: 0.5 }}>
          Tüm teklifler ve satış performansı
        </Typography>
      </Box>

      {/* ── Genel Özet ── */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
        <StatCard label="Toplam Teklif" value={overall.totalOffers} color={BRAND} icon={<Description />} />
        <StatCard label="Kabul Oranı" value={`%${overall.acceptanceRate}`} color="#16A34A" icon={<CheckCircle />} sub="Kabul / Toplam" />
        <StatCard label="Ort. Kar Marjı" value={`%${overall.avgProfitMargin}`} color="#B45309" icon={<TrendingUp />} />
        <StatCard label="Toplam Gelir" value={`$${fmt(overall.totalRevenue)}`} color={BRAND} icon={<TrendingUp />} sub="Kabul edilen teklifler" />
        <StatCard label="Müşteri" value={overall.totalCustomers} color="#7C3AED" icon={<People />} />
      </Box>

      {/* ── Kullanıcı Performansı ── */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `1px solid ${BORDER}`, borderRadius: '10px', bgcolor: SURFACE }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.5 }}>
          <Box sx={{
            width: 36, height: 36, bgcolor: '#eff6ff', borderRadius: '8px',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Person sx={{ color: BRAND, fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{
              fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em',
              textTransform: 'uppercase', color: BRAND
            }}>
              Satış Elemanı Performansı
            </Typography>
            <Typography sx={{ fontSize: '11px', color: TEXT2, mt: 0.2 }}>
              Sayılara tıklayarak tekliflere filtreli gidin
            </Typography>
          </Box>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Ad Soyad', 'Toplam', 'Taslak', 'Gönderildi', 'Kabul', 'Red', 'Kabul Oranı', 'Ort. Kar %', 'Toplam Gelir ($)'].map(h => (
                  <TableCell key={h} align={h === 'Ad Soyad' ? 'left' : h === 'Toplam Gelir ($)' ? 'right' : 'center'}
                             style={{ background: '#f8f9fc', paddingTop: 12, paddingBottom: 12,
                               fontSize: 11, fontWeight: 600, color: '#8a92a6',
                               textTransform: 'uppercase', letterSpacing: '0.05em',
                               borderBottom: '1px solid #e8eaf0' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {userStats.map(u => (
                <TableRow key={u.username}
                          sx={{
                            transition: 'background .1s',
                            '& td': { borderBottom: '1px solid #e8eaf0', py: 1.2 },
                            '&:hover td': { bgcolor: '#fafbfe !important' },
                          }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{
                        width: 34, height: 34, borderRadius: '10px',
                        bgcolor: avatarColor(u.fullName),
                        fontSize: '12px', fontWeight: 700
                      }}>
                        {getInitials(u.fullName)}
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: theme.palette.text.primary }}>
                          {u.fullName}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: TEXT2, fontFamily: 'monospace' }}>
                          @{u.username}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <ClickChip onClick={() => goOffers({ user: u.fullName })} label={u.totalOffers} variant="brand" />
                  </TableCell>
                  <TableCell align="center">
                    <ClickChip onClick={() => goOffers({ user: u.fullName, status: 'DRAFT' })} label={u.draftOffers} variant="warning" />
                  </TableCell>
                  <TableCell align="center">
                    <ClickChip onClick={() => goOffers({ user: u.fullName, status: 'SENT' })} label={u.sentOffers} variant="info" />
                  </TableCell>
                  <TableCell align="center">
                    <ClickChip onClick={() => goOffers({ user: u.fullName, status: 'ACCEPTED' })} label={u.acceptedOffers} variant="success" />
                  </TableCell>
                  <TableCell align="center">
                    <ClickChip onClick={() => goOffers({ user: u.fullName, status: 'REJECTED' })} label={u.rejectedOffers} variant="error" />
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 140 }}><RateBar rate={u.acceptanceRate} /></TableCell>
                  <TableCell align="center">
                    <Chip label={`%${u.avgProfitMargin}`} size="small"
                          sx={{ height: 22, bgcolor: '#fff7ed', color: '#B45309',
                            fontWeight: 700, fontSize: '11px', borderRadius: '6px',
                            border: '1px solid #fed7aa' }} />
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{
                      fontSize: '13px', fontWeight: 700, fontFamily: 'monospace',
                      color: u.totalRevenue > 0 ? '#16A34A' : 'text.disabled'
                    }}>
                      {u.totalRevenue > 0 ? `$${fmt(u.totalRevenue)}` : '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── Müşteri Analizi ── */}
      <Paper elevation={0} sx={{ p: 3, border: `1px solid ${BORDER}`, borderRadius: '10px', bgcolor: SURFACE }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.5 }}>
          <Box sx={{
            width: 36, height: 36, bgcolor: '#faf5ff', borderRadius: '8px',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <People sx={{ color: '#7C3AED', fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{
              fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em',
              textTransform: 'uppercase', color: '#7C3AED'
            }}>
              Müşteri Analizi
            </Typography>
            <Typography sx={{ fontSize: '11px', color: TEXT2, mt: 0.2 }}>
              Sayılara tıklayarak tekliflere filtreli gidin
            </Typography>
          </Box>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Müşteri', 'Toplam Teklif', 'Bekleyen', 'Kabul', 'Red', 'Kabul Oranı', 'Toplam Ciro ($)'].map(h => (
                  <TableCell key={h} align={h === 'Müşteri' ? 'left' : h === 'Toplam Ciro ($)' ? 'right' : 'center'}
                             style={{ background: '#f8f9fc', paddingTop: 12, paddingBottom: 12,
                               fontSize: 11, fontWeight: 600, color: '#8a92a6',
                               textTransform: 'uppercase', letterSpacing: '0.05em',
                               borderBottom: '1px solid #e8eaf0' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {customerStats.map(c => (
                <TableRow key={c.companyName}
                          sx={{
                            transition: 'background .1s',
                            '& td': { borderBottom: '1px solid #e8eaf0', py: 1.2 },
                            '&:hover td': { bgcolor: '#fafbfe !important' },
                          }}>
                  <TableCell>
                    <Typography onClick={() => goOffers({ customer: c.companyName })}
                                sx={{
                                  fontSize: '13px', fontWeight: 700, color: BRAND,
                                  cursor: 'pointer',
                                  '&:hover': { textDecoration: 'underline' }
                                }}>
                      {c.companyName}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <ClickChip onClick={() => goOffers({ customer: c.companyName })} label={c.totalOffers} variant="brand" />
                  </TableCell>
                  <TableCell align="center">
                    <ClickChip onClick={() => goOffers({ customer: c.companyName, status: 'DRAFT' })} label={c.pendingOffers} variant="warning" />
                  </TableCell>
                  <TableCell align="center">
                    <ClickChip onClick={() => goOffers({ customer: c.companyName, status: 'ACCEPTED' })} label={c.acceptedOffers} variant="success" />
                  </TableCell>
                  <TableCell align="center">
                    <ClickChip onClick={() => goOffers({ customer: c.companyName, status: 'REJECTED' })} label={c.rejectedOffers} variant="error" />
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 140 }}><RateBar rate={c.acceptanceRate} /></TableCell>
                  <TableCell align="right">
                    <Typography sx={{
                      fontSize: '13px', fontWeight: 700, fontFamily: 'monospace',
                      color: c.totalRevenue > 0 ? '#16A34A' : 'text.disabled'
                    }}>
                      {c.totalRevenue > 0 ? `$${fmt(c.totalRevenue)}` : '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default StatsPage;
